<template>
    <b-modal
        ref="modal"
        title="보고서 생성"
        size="sm"
        body-class="report-create-modal-body"
        header-class="report-create-modal-header"
        footer-class="report-create-modal-footer"
        cancel-title="닫기"
        ok-title="보고서 생성"
        :ok-disabled="creating || !canCreate"
        cancel-variant="secondary"
        ok-variant="create-carbon-report"
        @ok="handleCreate"
        @hidden="handleHidden"
    >
        <div class="report-create-content">
            <!-- 워크플로우: 연한 초록 박스 -->
            <div class="report-context-box">
                <span class="report-context-value">{{ workflowName || "—" }}</span>
            </div>

            <!-- 1. 보고서 옵션: 선택 시 초록 배경·흰 체크, 미선택 시 회색 원 -->
            <div class="create-section">
                <div class="section-label">보고서 옵션</div>
                <div class="option-pills">
                    <button
                        type="button"
                        class="option-pill"
                        :class="{ 'is-selected': createForm.reportOptions.includeEvidence }"
                        @click="createForm.reportOptions.includeEvidence = !createForm.reportOptions.includeEvidence"
                    >
                        <i class="fas fa-check-circle pill-icon" v-if="createForm.reportOptions.includeEvidence"></i>
                        <i class="fas fa-circle pill-icon-empty" v-else></i>
                        <span>증빙 자료 포함</span>
                    </button>
                    <button
                        type="button"
                        class="option-pill"
                        :class="{ 'is-selected': createForm.reportOptions.includeTargetStatus }"
                        @click="
                            createForm.reportOptions.includeTargetStatus = !createForm.reportOptions.includeTargetStatus
                        "
                    >
                        <i
                            class="fas fa-check-circle pill-icon"
                            v-if="createForm.reportOptions.includeTargetStatus"
                        ></i>
                        <i class="fas fa-circle pill-icon-empty" v-else></i>
                        <span>목표 현황 포함</span>
                    </button>
                    <button
                        type="button"
                        class="option-pill"
                        :class="{ 'is-selected': createForm.reportOptions.includeAuditHistory }"
                        @click="
                            createForm.reportOptions.includeAuditHistory = !createForm.reportOptions.includeAuditHistory
                        "
                    >
                        <i
                            class="fas fa-check-circle pill-icon"
                            v-if="createForm.reportOptions.includeAuditHistory"
                        ></i>
                        <i class="fas fa-circle pill-icon-empty" v-else></i>
                        <span>감사 이력 포함</span>
                    </button>
                </div>
            </div>

            <!-- 2. 유형 -->
            <div class="create-section">
                <label class="form-label">유형</label>
                <b-form-select
                    v-model="createForm.reportType"
                    :options="reportContentTypeOptions"
                    value-field="value"
                    text-field="text"
                    size="sm"
                    class="form-select form-select-type"
                />
            </div>

            <!-- 3. 언어: 탭 스타일 (유형 행 밑) -->
            <div class="create-section">
                <label class="form-label">언어</label>
                <div class="language-tabs">
                    <button
                        v-for="opt in languageOptions"
                        :key="opt.value"
                        type="button"
                        class="language-tab"
                        :class="{ 'is-selected': createForm.language === opt.value }"
                        @click="createForm.language = opt.value"
                    >
                        {{ opt.text }}
                    </button>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
import CarbonCodes from "@src/submodules/carbon-plugin/codes/carbonCodes";
import carbonApi from "../../../api";
import { unwrapResponse, extractErrorMessage } from "../../../utils/api.js";

const LANGUAGE_OPTIONS = [
    { value: "ko", text: "한국어" },
    { value: "en", text: "English" },
];

export default {
    name: "ReportCreateModal",
    props: {
        workflowIdx: { type: Number, default: null },
        workflowName: { type: String, default: "" },
    },
    data() {
        return {
            creating: false,
            createForm: {
                reportType: "GHG_INVENTORY",
                language: "ko",
                reportOptions: { includeEvidence: true, includeTargetStatus: false, includeAuditHistory: false },
            },
            lastCreatedReport: null,
            languageOptions: LANGUAGE_OPTIONS,
        };
    },
    computed: {
        canCreate() {
            return this.workflowIdx != null && this.workflowIdx > 0;
        },
        reportContentTypeOptions() {
            return CarbonCodes.reportContentType.getList();
        },
    },
    methods: {
        show() {
            this.lastCreatedReport = null;
            this.$refs.modal.show();
        },
        close() {
            this.$refs.modal.hide();
        },
        handleHidden() {
            this.lastCreatedReport = null;
            this.$emit("hidden");
        },

        handleCreate() {
            if (!this.canCreate || this.creating) return;
            this.creating = true;
            this.lastCreatedReport = null;
            const body = {
                workflowIdx: this.workflowIdx,
                reportType: this.createForm.reportType,
                format: "pdf",
                language: this.createForm.language,
                reportOptions: this.createForm.reportOptions,
            };
            carbonApi.report
                .createReport(body)
                .then((res) => {
                    const responseMessage = res?.data?.message;
                    const data = unwrapResponse(res, { defaultMessage: "생성에 실패했습니다." });
                    const created = data && (data.reportIdx != null ? data : data.data || data);
                    if (created && created.reportIdx) {
                        this.lastCreatedReport = created;
                        this.$bvToast.toast(created.title || "보고서가 생성되었습니다.", {
                            variant: "success",
                            title: responseMessage || "생성 완료",
                        });
                        return this.downloadCreated();
                    }
                    this.$bvToast.toast(created?.title || "보고서가 생성되었습니다.", {
                        variant: "success",
                        title: responseMessage || "생성 완료",
                    });
                })
                .catch((e) => {
                    this.$bvToast.toast(extractErrorMessage(e, "보고서 생성에 실패했습니다."), {
                        variant: "danger",
                        title: "생성 실패",
                    });
                })
                .finally(() => {
                    this.creating = false;
                });
        },
        async downloadCreated() {
            if (!this.lastCreatedReport) return;
            const r = this.lastCreatedReport;
            try {
                const blob = await carbonApi.report.downloadReport(r.reportIdx);
                const ext = "pdf";
                const url = URL.createObjectURL(blob.data || blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = (r.title || "report-" + r.reportIdx).replace(/[<>:"/\\|?*]/g, "_") + "." + ext;
                a.click();
                URL.revokeObjectURL(url);
            } catch (e) {
                this.$bvToast.toast(extractErrorMessage(e, "다운로드에 실패했습니다."), { variant: "danger" });
            }
        },
    },
};
</script>

<style scoped>
.report-create-content {
    font-size: 12px;
    color: #1e293b;
}

/* 워크플로우: 연한 초록 박스 */
.report-context-box {
    margin-bottom: 16px;
    padding: 10px 14px;
    background: #dcfce7;
    border: 1px solid #bbf7d0;
    border-radius: 8px;
}

.report-context-value {
    font-size: 12px;
    font-weight: 600;
    color: #166534;
}

.create-section {
    margin-bottom: 16px;
}

/* 섹션/필드 타이틀 통일 (보고서 형식, 보고서 옵션, 유형, 언어) */
.section-label,
.form-label {
    font-size: 12px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
}

.section-label {
    display: block;
}

.form-select {
    font-size: 12px;
}

.form-select-type {
    max-width: 100%;
}

.option-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
}

.option-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    font-size: 12px;
    color: #475569;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}

.option-pill:hover {
    background: #e2e8f0;
}

.option-pill.is-selected {
    border-color: #10b981;
    background: #10b981;
    color: #fff;
}

.option-pill .pill-icon {
    color: #fff;
    font-size: 12px;
}

.option-pill:not(.is-selected) .pill-icon-empty {
    color: #94a3b8;
    font-size: 10px;
}

.form-label {
    display: block;
}

/* 언어: 탭 스타일 (나머지와 어울리는 배경) */
.language-tabs {
    display: inline-flex;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
}

.language-tab {
    padding: 6px 16px;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    background: transparent;
    border: none;
    border-right: 1px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s;
}

.language-tab:last-child {
    border-right: none;
}

.language-tab:hover {
    background: #e2e8f0;
    color: #1e293b;
}

.language-tab.is-selected {
    background: #fff;
    color: #1e293b;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.report-create-modal-body {
    padding: 16px 18px;
}

.report-create-modal-header {
    font-size: 14px;
    font-weight: 700;
}
</style>
